import { useCallback, useSyncExternalStore, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { descriptionSymbol, getDescriptionSnapshot, subscribeDescription } from './store';
export { refreshDescription } from './store';
export type { DescriptionRecord } from './store';

/** Company-wide summary. Sector roles stay separate; missing evidence is visible. */
export function StockDescription({ symbol: raw, inline = false, className }: {
  symbol?: string | null; inline?: boolean; className?: string;
}) {
  const symbol = descriptionSymbol(raw);
  const subscribe = useCallback((fn: () => void) => subscribeDescription(symbol, fn), [symbol]);
  const snapshot = useSyncExternalStore(subscribe, () => getDescriptionSnapshot(symbol), () => getDescriptionSnapshot(symbol));
  if (!symbol) return null;
  const record = snapshot.record;
  const label = snapshot.state === 'loading' ? '简介读取中…'
    : snapshot.state === 'error' ? '简介读取失败'
    : record?.description || '简介待补充';
  const source = record?.source;
  const title = [label, source?.label, source?.note, source?.url, source?.provider && `模型：${source.provider} / ${source.model || "未记录"}`,
    source?.revenue_period && `营收报告期：${source.revenue_period}`, source?.observed_at && `来源时间：${source.observed_at}`,
    record?.updated_at && `维护时间：${record.updated_at}`,
    source?.review_required && '自动资料尚待人工核验；可在系统 / 个股简介数据库修订',
  ].filter(Boolean).join('\n');
  return <span data-stock-description={symbol} title={title}
    className={cn('stock-description font-sans text-[10px] font-normal leading-snug tracking-normal text-muted-foreground whitespace-normal break-words', inline ? 'ml-1 inline' : 'mt-0.5 block line-clamp-2', className)}>
    {inline && '（'}{label}{source?.review_required && record?.description && <span className="ml-1 opacity-70">· 待核验</span>}{inline && '）'}
  </span>;
}

/** Keeps the existing link/button and name styling; batches summaries by symbol. */
export function StockName({ symbol, name, children, inline = false, className }: {
  symbol?: string | null; name?: ReactNode; children?: ReactNode; inline?: boolean; className?: string;
}) {
  return <span className={cn('stock-identity min-w-0', className)} data-stock-identity={descriptionSymbol(symbol) || undefined}>
    <span data-stock-name>{children ?? name ?? symbol}</span><StockDescription symbol={symbol} inline={inline} />
  </span>;
}

export function StockOptionName({symbol:raw,name}:{symbol?:string|null;name?:string|null}) {
  const symbol=descriptionSymbol(raw);
  const subscribe=useCallback((fn:()=>void)=>subscribeDescription(symbol,fn),[symbol]);
  const snapshot=useSyncExternalStore(subscribe,()=>getDescriptionSnapshot(symbol),()=>getDescriptionSnapshot(symbol));
  const description=snapshot.state==='ready' ? snapshot.record?.description||'简介待补充' : snapshot.state==='error'?'简介读取失败':'简介读取中…';
  return <>{name||raw}{symbol&&`（${description}${snapshot.record?.source.review_required?' · 待核验':''}）`}</>;
}

export function StockList({symbols,nameOf,empty='—'}:{symbols:string[];nameOf?:(symbol:string)=>ReactNode;empty?:string}) {
  return <>{symbols.length ? symbols.map((symbol,i)=><span key={`${symbol}:${i}`}>{i>0?'、':''}<StockName symbol={symbol} name={nameOf?.(symbol)??symbol} inline /></span>) : empty}</>;
}
